import { isAxiosError, type AxiosError, type AxiosInstance } from "axios"

import { useSeamDevTools, type ApiCall } from "./use-seam-dev-tools"

const CALL_ID = "_seam_dev_tools_call_id"
const STARTED_AT = "_seam_dev_tools_started_at"

type RecordedConfig = { [CALL_ID]?: string; [STARTED_AT]?: number }

/**
 * Records every request `client` makes in the Seam devtools store. It adds
 * interceptors and removes none: `@seamapi/http` registers axios-retry and
 * its error interceptor on the response side, and both have to keep running.
 * Ours runs after them, so a failed call reaches it as a Seam error whose
 * `cause` is the original axios error.
 */
export function attachSeamDevToolsRecorder(
  client: AxiosInstance,
  workspaceId: string | null
): void {
  if (typeof window === "undefined") return
  client.interceptors.request.use((config) => {
    const recorded = config as typeof config & RecordedConfig
    const { isRecording, startCall, markRetried } = useSeamDevTools.getState()
    const existingId = recorded[CALL_ID]
    // axios-retry sends the same config again.
    if (existingId != null) {
      markRetried(existingId)
      return config
    }
    if (!isRecording) return config
    const id = crypto.randomUUID()
    recorded[CALL_ID] = id
    recorded[STARTED_AT] = Date.now()
    startCall({
      id,
      method: (config.method ?? "get").toUpperCase(),
      path: config.url ?? "",
      body: parseBody(config.data),
      workspaceId,
    })
    return config
  })

  client.interceptors.response.use(
    (response) => {
      complete(response.config as RecordedConfig, {
        state: "success",
        status: response.status,
        response: response.data,
        requestId: requestIdOf(response.headers),
      })
      return response
    },
    (error: unknown) => {
      const axiosError = findAxiosError(error)
      if (axiosError?.config != null) {
        complete(axiosError.config as RecordedConfig, {
          state: "error",
          status: axiosError.response?.status ?? null,
          response: axiosError.response?.data ?? null,
          requestId: requestIdOf(axiosError.response?.headers),
          errorMessage: axiosError.response == null ? axiosError.message : null,
        })
      }
      throw error
    }
  )
}

function complete(config: RecordedConfig, update: Partial<ApiCall>) {
  const id = config[CALL_ID]
  if (id == null) return
  const startedAt = config[STARTED_AT]
  useSeamDevTools.getState().completeCall(id, {
    ...update,
    durationMs: startedAt == null ? null : Date.now() - startedAt,
  })
}

/** The axios error itself, or the one a Seam error wraps as its `cause`. */
function findAxiosError(error: unknown): AxiosError | null {
  if (isAxiosError(error)) return error
  if (error instanceof Error && isAxiosError(error.cause)) return error.cause
  return null
}

function requestIdOf(
  headers: Record<string, unknown> | undefined
): string | null {
  const header = headers?.["seam-request-id"]
  return typeof header === "string" ? header : null
}

/** The body can arrive as a JSON string. */
function parseBody(data: unknown): unknown {
  if (typeof data !== "string") return data ?? null
  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}
